import {
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react"
import { useResourceUploadActivity } from "~/components/ResourceUploadField/UploadScope"
import { Flex, Spin, message } from "antd"
import classNames from "classnames"
import {
  fetchRemoteResourceFile,
  getRemoteResourceFetchErrorMessage,
} from "~/common/media/resourceUpload"
import {
  RESOURCE_UPLOAD_PASTE_PRIORITY,
  useResourceUploadInteractions,
  type PasteActivation,
} from "~/common/media/resourceUploadHooks"
import { Text } from "~/components/Text"
import styles from "./index.css"

export type UploaderProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "title" | "type"
> & {
  accept?: string
  allowRemoteImage?: boolean
  description?: ReactNode
  icon?: ReactNode
  loading?: boolean
  multiple?: boolean
  pasteActivation?: PasteActivation
  pastePriority?: number
  title: ReactNode
  onFiles: (files: File[]) => void
}

export const Uploader = ({
  accept,
  allowRemoteImage = false,
  className,
  description,
  disabled,
  icon,
  loading = false,
  multiple,
  onFiles,
  title,
  pasteActivation = "focus-or-hover",
  pastePriority = RESOURCE_UPLOAD_PASTE_PRIORITY.FIELD,
  onClick,
  ...props
}: UploaderProps) => {
  const [isFetching, setIsFetching] = useState(false)
  const request = useRef<AbortController | null>(null)
  useEffect(() => () => request.current?.abort(), [])
  useResourceUploadActivity(isFetching)
  const isLoading = loading || isFetching
  const isDisabled = disabled || isLoading

  const fetchImage = async (url: string) => {
    const controller = new AbortController()
    request.current = controller
    setIsFetching(true)

    try {
      const file = await fetchRemoteResourceFile({
        fallbackName: "远程图片",
        kind: "image",
        url,
        signal: controller.signal,
      })

      if (controller.signal.aborted) return
      onFiles([file])
    } catch (error) {
      if (controller.signal.aborted) return
      const errorMessage =
        error instanceof Error
          ? error.message
          : getRemoteResourceFetchErrorMessage("image")
      void message.error(errorMessage)
    } finally {
      if (!controller.signal.aborted) setIsFetching(false)
      if (request.current === controller) request.current = null
    }
  }

  const { inputProps, isDragging, openFileDialog, surfaceProps } =
    useResourceUploadInteractions({
      accept,
      allowRemoteImage,
      disabled: isDisabled,
      multiple,
      onFiles,
      onRemoteImageUrl: (url) => {
        void fetchImage(url)
      },
      pasteActivation,
      pastePriority,
    })

  return (
    <>
      <input
        {...inputProps}
        className={styles.input}
      />
      <button
        {...props}
        {...surfaceProps}
        aria-busy={isLoading || undefined}
        className={classNames(
          styles.root,
          className,
          isDragging && styles.dragging,
        )}
        disabled={isDisabled}
        onClick={onClick || openFileDialog}
        type="button"
      >
        <Flex
          className={styles.content}
          align="center"
          gap={4}
          justify="center"
          vertical
        >
          {isLoading ? (
            <Spin description={isFetching ? "获取远程素材中" : description} />
          ) : (
            <>
              {icon}
              <Text strong>{title}</Text>
              {description ? <Text type="secondary">{description}</Text> : null}
            </>
          )}
        </Flex>
      </button>
    </>
  )
}
